import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../design-system/cos-modal.js';
import '../design-system/cos-input.js';
import '../design-system/cos-textarea.js';
import '../design-system/cos-radio-group.js';
import '../design-system/cos-empty-state.js';
import './cos-skeleton.js';
import './cos-button.js';
import {
	searchImpersonationCandidates,
	startImpersonation,
	type ImpersonationCandidate,
} from '../services/impersonation.js';
import { userMessage } from '../utils/errorMessages.js';

/**
 * <cos-impersonate-dialog> — docs/impersonation-plan.md, the "Impersonate a
 * user…" flow launched from the avatar menu in cos-app-shell.
 *
 * Controlled like every other modal in this codebase (`open` is a prop the
 * consumer owns; see cos-modal's own doc comment) — this component never
 * flips it itself. Wraps `cos-modal` + `cos-input`/`cos-textarea`/
 * `cos-radio-group` — never a hand-rolled dialog/input/textarea/radio.
 *
 * ONE dialog, not a two-step wizard: search-and-pick a user (a combobox
 * over the currently-selected library's staff, always with a search field
 * — see the search-field note below), then a required reason and a
 * read-only/allow-changes choice, all visible in the same panel. Once a
 * candidate is picked the list collapses to a compact "chosen" row (with a
 * "Change" link back to search) so the reason/mode fields read as the next
 * and only remaining step.
 *
 * Events (plain names, not `cos:`-prefixed — that prefix names cos-modal's
 * OWN generic event, and this component's `cos:close` LISTENER already
 * catches every way it fires; matches cos-create-partner-modal.ts /
 * cos-snooze-modal.ts's identical close/created-style contract):
 *   - `close` — fired whenever cos-modal's own `cos:close` fires (backdrop,
 *     Esc, the header ×, or the standard footer's Cancel button), so the
 *     shell can flip its own `open`-controlling state; this component
 *     never closes itself.
 *   - `started` — fired right before the page reload that "Confirming
 *     calls start, then reloads into the target's view" requires
 *     (docs/impersonation-plan.md step 4). Reload happens inside this
 *     component via `window.location.reload()`; the event exists purely
 *     so a test can observe the moment without having to fake out a real
 *     navigation.
 */
@customElement('cos-impersonate-dialog')
export class CosImpersonateDialog extends LitElement {
	@property({ type: Boolean, reflect: true }) open = false;

	@state() private _query = '';
	@state() private _loading = false;
	@state() private _error = '';
	@state() private _candidates: ImpersonationCandidate[] = [];
	@state() private _active = 0;
	@state() private _selected: ImpersonationCandidate | null = null;
	@state() private _reason = '';
	@state() private _allowWrites = false;
	@state() private _submitting = false;
	@state() private _submitError = '';

	private _searchTimer: ReturnType<typeof setTimeout> | undefined;
	// Guards against a slow, stale response landing after a faster, more
	// recent one — the same class of race every debounced search in this
	// codebase has to account for.
	private _searchSeq = 0;

	static styles = css`
		:host {
			display: contents;
		}
		.intro {
			margin: 0 0 var(--cos-space-3);
			font-size: var(--cos-font-size-sm);
			color: var(--cos-text-secondary);
		}
		.list {
			display: flex;
			flex-direction: column;
			gap: 2px;
			max-height: 240px;
			overflow-y: auto;
			border: 1px solid var(--cos-border);
			border-radius: var(--cos-radius-md);
			padding: 4px;
			margin-bottom: var(--cos-space-4);
		}
		.opt {
			display: flex;
			flex-direction: column;
			gap: 2px;
			padding: var(--cos-space-2) var(--cos-space-3);
			border-radius: var(--cos-radius-sm);
			cursor: pointer;
		}
		.opt.on {
			background: var(--cos-status-teal-wash, var(--cos-surface-raised));
		}
		.opt:hover {
			background: var(--cos-surface-raised);
		}
		.opt-name {
			font-size: var(--cos-font-size-sm);
			font-weight: var(--cos-font-weight-medium);
			color: var(--cos-text-primary);
		}
		.opt-meta {
			font-size: var(--cos-font-size-xs);
			color: var(--cos-text-tertiary);
		}
		.note {
			padding: var(--cos-space-3);
			font-size: var(--cos-font-size-sm);
			color: var(--cos-text-tertiary);
		}
		.error-note {
			padding: var(--cos-space-3);
			font-size: var(--cos-font-size-sm);
			color: var(--cos-status-danger-text);
		}
		.chosen {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: var(--cos-space-3);
			padding: var(--cos-space-3);
			margin-bottom: var(--cos-space-4);
			border: 1px solid var(--cos-border);
			border-radius: var(--cos-radius-md);
			background: var(--cos-surface-raised);
		}
		.chosen-name {
			font-size: var(--cos-font-size-sm);
			font-weight: var(--cos-font-weight-medium);
			color: var(--cos-text-primary);
		}
		.chosen-meta {
			font-size: var(--cos-font-size-xs);
			color: var(--cos-text-tertiary);
		}
		.fields {
			display: flex;
			flex-direction: column;
			gap: var(--cos-space-4);
		}
		.submit-error {
			font-size: var(--cos-font-size-sm);
			color: var(--cos-status-danger-text);
		}
	`;

	updated(changed: Map<string, unknown>): void {
		super.updated(changed);
		if (changed.has('open') && this.open) {
			this._reset();
			void this._search('');
		}
	}

	private _reset(): void {
		this._query = '';
		this._candidates = [];
		this._active = 0;
		this._selected = null;
		this._reason = '';
		this._allowWrites = false;
		this._submitting = false;
		this._submitError = '';
		this._error = '';
	}

	private _scheduleSearch(q: string): void {
		clearTimeout(this._searchTimer);
		this._searchTimer = setTimeout(() => void this._search(q), 300);
	}

	private async _search(q: string): Promise<void> {
		const seq = ++this._searchSeq;
		this._loading = true;
		this._error = '';
		try {
			const results = await searchImpersonationCandidates(q);
			if (seq !== this._searchSeq) return; // a newer search already landed
			this._candidates = results;
			this._active = 0;
		} catch (err) {
			if (seq !== this._searchSeq) return;
			this._candidates = [];
			this._error = userMessage(err).description;
		} finally {
			if (seq === this._searchSeq) this._loading = false;
		}
	}

	private _pick(c: ImpersonationCandidate): void {
		this._selected = c;
	}

	private _onSearchInput(e: CustomEvent<{ value: string }>): void {
		this._query = e.detail.value;
		this._scheduleSearch(this._query);
	}

	private _onSearchKeydown(e: KeyboardEvent): void {
		if (this._candidates.length === 0) return;
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			this._active = Math.min(this._active + 1, this._candidates.length - 1);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			this._active = Math.max(this._active - 1, 0);
		} else if (e.key === 'Enter') {
			const c = this._candidates[this._active];
			if (c) {
				e.preventDefault();
				this._pick(c);
			}
		}
	}

	private _onModalClose(e: CustomEvent<{ reason: string }>): void {
		// Plain 'close', not 'cos:close' — cos-modal's OWN cos:close already
		// bubbles+composes all the way out past this component regardless
		// (nothing here stops it), so re-dispatching under the SAME name
		// would double-fire on any listener attached to this element. The
		// established fix in every other modal wrapper in this codebase
		// (cos-create-partner-modal.ts, cos-snooze-modal.ts, …) is a
		// distinctly-named outward event.
		this.dispatchEvent(new CustomEvent('close', { detail: e.detail, bubbles: true, composed: true }));
	}

	private get _canSubmit(): boolean {
		return !!this._selected && this._reason.trim().length > 0 && !this._submitting;
	}

	private async _onPrimary(): Promise<void> {
		if (!this._selected || !this._canSubmit) return;
		this._submitting = true;
		this._submitError = '';
		try {
			await startImpersonation(this._selected.id, this._reason.trim(), this._allowWrites);
			this.dispatchEvent(new CustomEvent('started', { bubbles: true, composed: true }));
			// "Confirming calls start, then reloads into the target's view"
			// (docs/impersonation-plan.md step 4) — a full reload is the
			// simplest way to guarantee every subsequent fetch on the page
			// (including whatever the current route already loaded under
			// the real identity) is re-issued under the target's effective
			// identity from the very first request.
			window.location.reload();
		} catch (err) {
			this._submitting = false;
			this._submitError = userMessage(err).description;
		}
	}

	private _renderPicker() {
		if (this._loading && this._candidates.length === 0) {
			return html`<div class="list"><cos-skeleton variant="row" count="3"></cos-skeleton></div>`;
		}
		if (this._error) {
			return html`<div class="error-note">${this._error}</div>`;
		}
		if (this._candidates.length === 0) {
			return html`
				<cos-empty-state
					tone="quiet"
					headingLevel="h3"
					title="No matching users"
					description=${this._query ? `No staff match "${this._query}" at this library.` : 'This library has no staff to impersonate yet.'}
				></cos-empty-state>
			`;
		}
		return html`
			<div class="list" role="listbox" aria-label="Library users">
				${this._candidates.map(
					(c, i) => html`
						<div
							class="opt ${i === this._active ? 'on' : ''}"
							role="option"
							aria-selected=${i === this._active ? 'true' : 'false'}
							@mousedown=${(e: MouseEvent) => {
								e.preventDefault();
								this._pick(c);
							}}
						>
							<span class="opt-name">${c.name}</span>
							<span class="opt-meta">${c.email}${c.role ? ` · ${c.role}` : ''}</span>
						</div>
					`,
				)}
			</div>
		`;
	}

	render() {
		return html`
			<cos-modal
				.open=${this.open}
				heading="Impersonate a user"
				size="md"
				.primaryLabel=${'Start impersonating'}
				.primaryDisabled=${!this._canSubmit}
				.primaryLoading=${this._submitting}
				@cos:close=${this._onModalClose}
				@cos:primary=${this._onPrimary}
			>
				${this._selected
					? html`
							<div class="chosen">
								<div>
									<div class="chosen-name">${this._selected.name}</div>
									<div class="chosen-meta">${this._selected.email}${this._selected.role ? ` · ${this._selected.role}` : ''}</div>
								</div>
								<cos-button variant="link" size="sm" @click=${() => { this._selected = null; }}>Change</cos-button>
							</div>
						`
					: html`
							<p class="intro">Choose which of this library's users to view CommunicoOS as.</p>
							<!--
								Deliberately ALWAYS rendered, even under ten users — Paul's
								flow asked for search "if over 10 users", but a control that
								appears and disappears depending on row count reads as a bug,
								and the field is a harmless no-op (matches everything) when
								there's nothing to filter. Flagging this choice per the brief
								so it can be overruled.
							-->
							<cos-input
								type="search"
								label="Search users"
								hide-label
								placeholder="Search by name or email…"
								clearable
								.value=${this._query}
								@cos:input=${this._onSearchInput}
								@keydown=${this._onSearchKeydown}
							></cos-input>
							${this._renderPicker()}
						`}
				<div class="fields">
					<cos-textarea
						label="Reason"
						required
						placeholder="Why are you impersonating this user? (support ticket, training session, …)"
						.value=${this._reason}
						@cos:input=${(e: CustomEvent<{ value: string }>) => { this._reason = e.detail.value; }}
					></cos-textarea>
					<cos-radio-group
						label="What can you do in this session?"
						.options=${[
							{ value: 'read', label: 'Read-only', helpText: 'See exactly what they see. No changes can be saved.' },
							{ value: 'write', label: 'Allow changes', helpText: 'Everything you do is attributed to you and logged.' },
						]}
						.value=${this._allowWrites ? 'write' : 'read'}
						@cos:change=${(e: CustomEvent<{ value: string }>) => { this._allowWrites = e.detail.value === 'write'; }}
					></cos-radio-group>
					${this._submitError ? html`<div class="submit-error">${this._submitError}</div>` : nothing}
				</div>
			</cos-modal>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-impersonate-dialog': CosImpersonateDialog;
	}
}
